/* global Intercom */
import { useRef, useState } from "react";
import PropTypes from "prop-types";
import IconButton from "@shared/v2/IconButton/IconButton";
import TextComboInput from "@shared/v2/TextComboInput";
import Tooltip from "@shared/v2/Tooltip";
import {
  ArrowUpRightFromSquareSolidV6,
  BookSolidV6,
  ChevronRightSolidV6,
  CircleQuestionRegularV6,
  GraduationCapRegularV6,
  HeadsetRegularV6,
  MagnifyingGlassSolidV6,
  MessageSmileRegularV6,
  MessagesRegularV6,
  SparklesRegularV6,
  VideoRegularV6,
} from "@shared/v2/Icomoon";
import { useClickOutside } from "@shared/hookHelpers";
import Popover from "../Popover";
import { useCurrentUser, useFeatureFlags } from "../../reducers/layoutReducer/selectors";

const HelpButton = ({
  href = undefined,
  icon = null,
  isAi = false,
  label,
  onClick = undefined,
  ...other
}) => {
  const Element = href ? "a" : "button";
  const props = href
    ? { href, target: "_blank", rel: "noopener noreferrer", ...other }
    : { type: "button", ...other };
  return (
    <Element
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      className={`tw-group tw-bg-white ${isAi ? "hover:tw-bg-semantic-purple-5 tw-text-semantic-purple-100 hover:tw-text-[#572bad]" : "hover:tw-bg-gray-5 tw-text-gray-75 hover:tw-text-gray-99"} hover:tw-no-underline tw-rounded-[8px] tw-border-none tw-px-[12px] tw-py-[8px] tw-flex tw-gap-[8px] tw-items-center tw-text-left`}
      onClick={onClick}
    >
      {icon}
      <span className="tw-flex-1 tw-text-14d tw-font-bold">{label}</span>
      {href && <ArrowUpRightFromSquareSolidV6 className="tw-invisible group-hover:tw-visible" />}
    </Element>
  );
};

HelpButton.propTypes = {
  href: PropTypes.string,
  icon: PropTypes.node,
  isAi: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

const Help = () => {
  const featureFlags = useFeatureFlags();
  const { isPlace, isLever } = useCurrentUser().account;
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  useClickOutside(ref, () => setOpen(false));

  return (
    <div ref={ref} className="tw-relative">
      <Tooltip
        // @ts-expect-error props
        innerClassName="!tw-bg-neutral-gray-75"
        arrowClassName="tw-fill-neutral-gray-75"
        trigger={
          <IconButton
            className={`${isPlace || isLever ? "tw-text-white" : ""}  hover:tw-bg-gray/20 active:tw-bg-gray/30`}
            schema="transparent"
            id="helpDropdown"
            aria-haspopup="true"
            aria-expanded={open}
            aria-label="Help Center"
            onClick={() => setOpen((o) => !o)}
          >
            <CircleQuestionRegularV6 size="xxl" />
          </IconButton>
        }
        content="Help Center"
        placement="bottom"
      />

      <Popover
        aria-labelledby="helpDropdown"
        className="tw-w-[326px] tw-z-3000 tw-p-[24px] tw-flex tw-flex-col tw-gap-[16px]"
        open={open}
      >
        <h2 className="tw-text-18d tw-font-bold tw-text-gray-75 tw-m-0 tw-flex tw-items-center tw-gap-[4px]">
          <CircleQuestionRegularV6 size="xl" /> Help
        </h2>
        <form
          className="tw-flex tw-gap-[6px]"
          action="https://www.brivityknowledge.com/v1/docs/search"
          method="get"
          target="_blank"
          onSubmit={() => setOpen(false)}
        >
          <TextComboInput
            containerClassName="tw-flex-1"
            name="searchtext"
            placeholder="Search for help"
            trailing={<MagnifyingGlassSolidV6 size="l" className="tw-text-gray-30" />}
          />
          <button
            type="submit"
            className="tw-w-[36px] tw-h-[36px] tw-flex tw-justify-center tw-items-center tw-rounded-[4px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-bg-white tw-text-gray-75 hover:tw-bg-gray-5"
          >
            <ChevronRightSolidV6 size="l" />
          </button>
        </form>
        <button
          type="button"
          className="tw-bg-gray-5 tw-rounded-[8px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-px-[12px] tw-py-[8px] tw-flex tw-gap-[8px] tw-items-center hover:tw-bg-theme-button-secondary-active hover:tw-border-theme-primary"
          onClick={() => {
            Intercom?.("showMessages");
            setOpen(false);
          }}
        >
          <div className="tw-w-[32px] tw-h-[32px] tw-flex tw-justify-center tw-items-center tw-flex-none tw-rounded-full tw-bg-theme-primary-alternative tw-text-white">
            <MessagesRegularV6 size="l" />
          </div>
          <div className="tw-flex-1 tw-flex tw-flex-col tw-text-left">
            <span className="tw-text-14d tw-font-bold tw-text-gray-75">Start a Live Chat</span>
            <span className="tw-text-12d tw-text-gray-75">Chat with our support team</span>
          </div>
          <ChevronRightSolidV6 />
        </button>
        <div className="tw-flex tw-flex-col">
          <h5 className="tw-px-[12px] tw-pb-[4px] tw-text-12d tw-font-semibold tw-text-gray-50 tw-normal-case tw-m-0">
            Find Answers
          </h5>
          {featureFlags.askPlace && (
            <HelpButton
              icon={<SparklesRegularV6 size="l" />}
              isAi
              label="Ask Gabbi AI"
              onClick={() => {
                document.querySelector('[aria-label="Open Ask Gabbi"]')?.click();
                setOpen(false);
              }}
            />
          )}
          <HelpButton
            href="https://brivityknowledge.com/"
            icon={<BookSolidV6 size="l" />}
            label="Knowledge Center"
            onClick={() => setOpen(false)}
          />
          <hr className="tw-w-full tw-my-[14px] tw-flex tw-flex-none tw-border-solid tw-border-t-[1px] tw-border-gray-10" />
          <h5 className="tw-px-[12px] tw-pb-[4px] tw-text-12d tw-font-semibold tw-text-gray-50 tw-normal-case tw-m-0">
            Stay Current
          </h5>
          <HelpButton
            href={`https://learn.${isPlace ? "place" : "brivity"}.com/`}
            icon={<GraduationCapRegularV6 size="l" />}
            label={`${isPlace ? "PLACE" : "Brivity"} Academy`}
            onClick={() => setOpen(false)}
          />
          <HelpButton
            href="https://www.brivityknowledge.com/docs/live-training"
            icon={<VideoRegularV6 size="l" />}
            label="Live Training Schedule"
            onClick={() => setOpen(false)}
          />
          <HelpButton
            icon={<MessageSmileRegularV6 size="l" />}
            id="intercom-give-feedback"
            label="Give Feedback"
            onClick={() => setOpen(false)}
          />
        </div>
        <a
          className="tw-group tw-bg-white tw-rounded-[8px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-px-[12px] tw-py-[8px] tw-flex tw-gap-[8px] tw-items-center hover:tw-bg-theme-button-secondary-active hover:tw-border-theme-primary hover:tw-no-underline"
          onClick={() => setOpen(false)}
          href="tel:8554274848"
        >
          <div className="tw-w-[32px] tw-h-[32px] tw-flex tw-justify-center tw-items-center tw-flex-none tw-rounded-full tw-bg-theme-button-secondary-active tw-text-theme-primary group-hover:tw-bg-theme-brand-lighter">
            <HeadsetRegularV6 size="l" />
          </div>
          <div className="tw-flex-1 tw-flex tw-flex-col tw-text-left">
            <span className="tw-text-14d tw-font-bold tw-text-theme-primary">(855) 427-4848</span>
            <span className="tw-text-12d tw-text-gray-75">Call Support · Mon–Fri 6am–5pm PST</span>
          </div>
        </a>
      </Popover>
    </div>
  );
};
export default Help;
